import { useContext, useEffect, useState } from 'react'
import RudderStackContext from 'context/common/RudderStackContext'
import UserContext from 'context/common/User/UserContext'
import useThriveAuth from 'hooks/useThriveAuth'
import useIsRolloutPlanSettled from 'hooks/treatments/useIsRolloutPlanSettled'
import {
  getUserTreatment,
  isSplitAnalyticsConsentGranted,
  userSplitClient,
} from 'utils/treatment'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'

// Extended booking is on for third-party agents (role) at Charlie Health
// (businessPartnerId). Same two attributes are checked on the server.
const THIRD_PARTY_SUPPORT_ROLE = 'thirdparty-support'

const useExtendedBookingTreatment = (): {
  enabled: boolean
  isLoading: boolean
} => {
  const { businessPartnerId, isUserLoading } = useContext(UserContext)
  // Module-level consent / userSplitClient are not reactive; context re-renders
  // so the effect re-resolves when consent flips after a rollout-plan ON.
  const { analyticsConsentGranted } = useContext(RudderStackContext)
  const { isThirdPartyAgent, isAuthenticated } = useThriveAuth()
  const rolloutPlanSettled = useIsRolloutPlanSettled()

  const role = isThirdPartyAgent ? THIRD_PARTY_SUPPORT_ROLE : ''
  const [treatment, setTreatment] = useState<string>(
    treatmentFlagValues.CONTROL
  )
  const [isSplitReady, setIsSplitReady] = useState(false)

  // Wait on the current client's ready() promise, not the shared
  // isUserSplitReady() flag, which stays stale-true after the post-booking
  // re-init. Read the value through getUserTreatment so consent-denied users
  // (who have no user Split client) still receive the rollout-plan released
  // value, matching the other treatment consumers.
  useEffect(() => {
    if (isUserLoading || !isAuthenticated) {
      return
    }

    const attributes = { businessPartnerId: businessPartnerId ?? '', role }

    if (!userSplitClient) {
      // Consent granted but no client yet: resolve immediately from the
      // fallback (control until SDK inits). Consent denied: wait for the
      // rollout plan so a released ON is not lost on the first read.
      if (!isSplitAnalyticsConsentGranted() && !rolloutPlanSettled) {
        return
      }

      setTreatment(
        getUserTreatment(
          TreatmentFlags.EXTENDED_BOOKING_VIRTUAL_SLOTS,
          attributes
        )
      )
      setIsSplitReady(true)
      return
    }

    setIsSplitReady(false)
    let isActive = true

    const resolveTreatment = () => {
      if (!isActive) {
        return
      }

      setTreatment(
        getUserTreatment(
          TreatmentFlags.EXTENDED_BOOKING_VIRTUAL_SLOTS,
          attributes
        )
      )
      setIsSplitReady(true)
    }

    userSplitClient.ready().then(resolveTreatment).catch(resolveTreatment)

    return () => {
      isActive = false
    }
  }, [
    isUserLoading,
    isAuthenticated,
    businessPartnerId,
    role,
    rolloutPlanSettled,
    analyticsConsentGranted,
  ])

  // Still resolving while the user loads, or (for logged-in users) until the
  // Split client is ready. Anonymous users have no Split value, so they're
  // never stuck loading.
  const isLoading = isUserLoading || (isAuthenticated && !isSplitReady)

  return { enabled: treatment === treatmentFlagValues.ON, isLoading }
}

export default useExtendedBookingTreatment
